import * as React from 'react';

import { POOL_CLUSTER } from 'defs/pools';
import { push as dataLayerPush } from 'helper/ga';
import i18n from 'helper/i18n';
import ClientStorage, { StorageTypes } from 'lib/ClientStorage';

import Radio from 'components/atoms/Radio';
import ElementList from 'components/molecules/ElementList';
import { FilterLayer } from 'components/molecules/FilterLayer';
import Overlay from 'components/molecules/Overlay';

import { trackAddFilter, trackRemoveFilter } from '../../../defs/ga/track/pool';
import { Checkbox } from '../../atoms/Checkbox/Checkbox';
import ALL_FILTERS from './factories';

export enum TagFilterItemTypesType {
	OVERLAY = 'overlay',
	TOGGLE = 'toggle',
}

export enum ItemTagType {
	CHECKBOX = 'checkbox',
	RADIO = 'radio',
}

export type TagFilterItemType = {
	filterId: string;
	type: TagFilterItemTypesType;
	tag: ItemTagType;
	value?: string[];
	labels: {
		active: React.ReactNode | string;
		inactive: React.ReactNode | string;
	};
};

export type TagFilterPropsType = {
	selectedPool: string;
	selectedCluster: string;
	description?: string;
	modifier?: string | string[];
	items: TagFilterItemType[];
	filters: { [filterId: string]: string[] };
	activeOverlayRef?: string;
	programItemRef?: string;
	doAddPersistentFilter: (ref: string, value: string) => void;
	doRemovePersistentFilter: (ref: string, value: string) => void;
	doSetPersistentFilter: (ref: string, value: string) => void;
	doUnsetPersistentFilter: (ref: string) => void;
	doSetProgramItemRef: (ref: string, programItemExternalRef?: string) => void;
	doUnsetProgramItemRef: (ref: string) => void;
	doSetOverlayRef: (ref: string) => void;
	doUnsetOverlayRef: (ref: string) => void;
	doUnsetCurrentOverlayRef: () => void;
};

// update filter in local storage
export const persistateFilterOption = (
	selectedPool: string,
	selectedCluster: string,
	filterKey: string,
	selectedOption: string,
	exclusive = true
) => {
	const SessionStorage = new ClientStorage(StorageTypes.SESSION);
	const poolKey = `pool-${selectedPool}-${selectedCluster}`;
	const initialFactory = SessionStorage.get(poolKey) || {};
	const selectedOptionExists = initialFactory[filterKey] && initialFactory[filterKey].includes(selectedOption);
	let updatedFilters: string[] = [];

	// radio buttons
	if (exclusive) {
		SessionStorage.set(poolKey, {
			...initialFactory,
			[filterKey]: [selectedOption],
		});
	}

	// checkboxes
	if (!exclusive) {
		// remove
		if (selectedOptionExists) {
			updatedFilters = initialFactory[filterKey].filter((key: string) => key !== selectedOption);
		}
		// empty
		if (selectedOption === 'NONE' || (selectedOptionExists && updatedFilters.length === 0)) {
			updatedFilters = ['NONE'];
		}
		// add
		if (!selectedOptionExists && selectedOption !== 'NONE') {
			updatedFilters = initialFactory[filterKey]
				? [...initialFactory[filterKey], selectedOption]
				: [selectedOption];
			updatedFilters = updatedFilters.filter((filter) => filter !== 'NONE');
		}

		SessionStorage.set(poolKey, {
			...initialFactory,
			[filterKey]: updatedFilters,
		});
	}
};

export class TagFilter extends React.Component<TagFilterPropsType> {
	public static displayName = 'TagFilter';

	public getSessionFilterKey(ref: string) {
		const { selectedPool, selectedCluster } = this.props;
		return `pool_${selectedPool}_${selectedCluster}_${ref}`;
	}

	public renderOverlay() {
		const {
			filters,
			selectedPool,
			selectedCluster,
			activeOverlayRef,
			doAddPersistentFilter,
			doRemovePersistentFilter,
			doSetPersistentFilter,
			doUnsetPersistentFilter,
			doUnsetCurrentOverlayRef,
			items,
		} = this.props;
		const allFilterKeys = activeOverlayRef
			? Object.keys(ALL_FILTERS[activeOverlayRef].radioLabels || ALL_FILTERS[activeOverlayRef].labels)
			: [];

		const hasUserSelectedOption = allFilterKeys
			.map((filterKey: string) =>
				activeOverlayRef
					? !!(filters[activeOverlayRef] && filters[activeOverlayRef].includes(filterKey))
					: false
			)
			.some((state: boolean) => state === true);

		const filterCategoryLabel = i18n(
			`react.impulsCoach.components.tagFilter.${selectedPool}.${activeOverlayRef || 'FALLBACK'}.placeholder`
		);

		const noneElementProps = {
			tag: 'li',
			id: 'NONE',
			name: activeOverlayRef!,
			label: i18n(`react.impulsCoach.components.tagFilter.unset`),
			onChange: () => {
				if (activeOverlayRef) {
					// labels may be of type `string` or `ReactNode`
					const labels = filters[activeOverlayRef].map((filterId) => {
						return typeof ALL_FILTERS[activeOverlayRef].labels[filterId] === 'string'
							? ALL_FILTERS[activeOverlayRef].labels[filterId]
							: filterId;
					});

					dataLayerPush(
						trackRemoveFilter({ filterName: filterCategoryLabel, filterSelected: labels.join(', ') })
					);
					doUnsetPersistentFilter(activeOverlayRef);

					if (selectedCluster === POOL_CLUSTER.ALL) {
						persistateFilterOption(selectedPool, selectedCluster, activeOverlayRef, 'NONE');
					}
				}
			},
		};
		const activeItem = items.find((item) => item.filterId === activeOverlayRef);

		const NoneElement =
			activeItem && activeItem.tag === ItemTagType.CHECKBOX ? (
				<Checkbox {...noneElementProps} isChecked={hasUserSelectedOption === false} modifier="white" />
			) : (
				<Radio {...noneElementProps} checked={hasUserSelectedOption === false} value={'NONE'} />
			);

		return (
			<Overlay
				contentStyle="notification"
				preventLock
				preventUnlock
				isOpen={!!activeOverlayRef}
				closable
				closeButton
				animation="move"
				onClose={() => {
					doUnsetCurrentOverlayRef();
				}}
			>
				<FilterLayer
					title={filterCategoryLabel}
					modifier={selectedPool}
					handler={() => {
						doUnsetCurrentOverlayRef();
					}}
				>
					<ElementList>
						{NoneElement}
						{activeOverlayRef &&
							allFilterKeys.map((filterLabelId: string) => {
								const config = ALL_FILTERS[activeOverlayRef];

								const isActive =
									!!filters[activeOverlayRef] && filters[activeOverlayRef].includes(filterLabelId);

								const filterLabel = config.radioLabels
									? config.radioLabels[filterLabelId]
									: config.labels[filterLabelId];

								const trackingFilterLabelValue =
									typeof filterLabel === 'string' ? filterLabel : filterLabelId;

								const filterElementProps = {
									key: filterLabelId,
									tag: 'li',
									id: filterLabelId,
									name: activeOverlayRef,
									label: filterLabel,
									onChange: () => {
										if (activeOverlayRef) {
											// filter type is checkbox
											if (activeItem && activeItem.tag === ItemTagType.CHECKBOX) {
												if (isActive) {
													dataLayerPush(
														trackRemoveFilter({
															filterName: filterCategoryLabel,
															filterSelected: trackingFilterLabelValue,
														})
													);
													doRemovePersistentFilter(activeOverlayRef, filterLabelId);
												} else {
													dataLayerPush(
														trackAddFilter({
															filterName: filterCategoryLabel,
															filterSelected: trackingFilterLabelValue,
														})
													);
													doAddPersistentFilter(activeOverlayRef, filterLabelId);
												}
											} else {
												// sets the filter and removes others (like a radio button should)
												dataLayerPush(
													trackAddFilter({
														filterName: filterCategoryLabel,
														filterSelected: trackingFilterLabelValue,
													})
												);
												doSetPersistentFilter(activeOverlayRef, filterLabelId);
											}

											if (selectedCluster === POOL_CLUSTER.ALL) {
												persistateFilterOption(
													selectedPool,
													selectedCluster,
													activeOverlayRef,
													filterLabelId,
													!!(activeItem && activeItem.tag !== ItemTagType.CHECKBOX)
												);
											}
										}
									},
								};

								const FilterTag =
									activeItem && activeItem.tag === ItemTagType.CHECKBOX ? (
										<Checkbox {...filterElementProps} isChecked={isActive} modifier="white" />
									) : (
										<Radio {...filterElementProps} checked={isActive} value={filterLabelId} />
									);

								return FilterTag;
							})}
					</ElementList>
				</FilterLayer>
			</Overlay>
		);
	}

	public render() {
		const {
			description,
			items,
			doSetOverlayRef,
			selectedPool,
			selectedCluster,
			doSetPersistentFilter,
			doUnsetPersistentFilter,
		} = this.props;

		return (
			<div className="m-tag-filter">
				{this.renderOverlay()}
				{description && <p className="m-tag-filter__description">{description}</p>}
				<ul className="m-tag-filter__list">
					{items.map((item: TagFilterItemType) => {
						const itemHasValidValue =
							item.value &&
							!item.value.includes('NONE') &&
							!item.value.includes('UNKNOWN') &&
							!item.value.includes('MEAT_FISH');

						return (
							<li key={item.filterId} className="m-tag-filter__item">
								<a
									className={`m-tag-filter__link${
										itemHasValidValue ? ' state-m-tag-filter__link--selected' : ''
									}`}
									onClick={(ev: React.MouseEvent<any>) => {
										ev.preventDefault();
										if (item.type === TagFilterItemTypesType.OVERLAY) {
											doSetOverlayRef(item.filterId);
										}
										// filter type is a toggle (button)
										if (item.type === TagFilterItemTypesType.TOGGLE) {
											if (selectedCluster === POOL_CLUSTER.ALL) {
												if (!item.value) {
													dataLayerPush(
														trackAddFilter({
															filterName: item.labels.inactive as string,
															filterSelected: true,
														})
													);
													doSetPersistentFilter(item.filterId, 'ACTIVE');
													persistateFilterOption(
														selectedPool,
														selectedCluster,
														item.filterId,
														'ACTIVE',
														true
													);
												} else {
													dataLayerPush(
														trackAddFilter({
															filterName: item.labels.active as string,
															filterSelected: false,
														})
													);
													doUnsetPersistentFilter(item.filterId);
													persistateFilterOption(
														selectedPool,
														selectedCluster,
														item.filterId,
														'NONE',
														true
													);
												}
											}
										}
									}}
								>
									<div className="m-tag-filter__label">
										{itemHasValidValue ? item.labels.active : item.labels.inactive}
									</div>
								</a>
							</li>
						);
					})}
				</ul>
			</div>
		);
	}
}

export default TagFilter;
